/**
 * The builder's form state and every edit to it, as plain functions over one draft, so step ids stay
 * unique, an argument only names an input that exists, and no step ends up waiting for itself. Inputs
 * come from arguments the AI fills in, and go when no argument uses them.
 */

import type { ToolField } from '@/modules/back-testing/datasets/tools/tool-schema';
import type {
	CustomTool,
	CustomToolArg,
	CustomToolInput,
	CustomToolInputType,
	CustomToolPayload,
	CustomToolStep,
	CustomToolSuggestion
} from '@/types/custom-tool.types';

export type CustomToolDraft = CustomToolPayload;

export const LIMITS = { name: 80, description: 1000, inputs: 10, minSteps: 2, steps: 10 } as const;

const SLUG = /^[a-z][a-z0-9_]{2,63}$/;
const RESERVED_SLUG_PREFIX = 'spreadsheet_search_';
const INPUT_NAME = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/;
const STEP_ID_LENGTH = 32;
export const STEP_PATH = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/;

export const emptyDraft = (): CustomToolDraft => ({
	name: '',
	slug: '',
	description: '',
	inputs: [],
	steps: []
});

export const draftFromTool = ({ name, slug, description, inputs, steps }: CustomTool): CustomToolDraft => ({
	name,
	slug,
	description,
	inputs,
	steps: steps.map((step) => ({ ...step, after: step.after ?? [] }))
});

export const toPayload = (draft: CustomToolDraft): CustomToolPayload => ({
	...draft,
	name: draft.name.trim(),
	description: draft.description.trim()
});

const keysInOrder = (_key: string, value: unknown): unknown =>
	value && typeof value === 'object' && !Array.isArray(value)
		? Object.fromEntries(Object.entries(value).sort(([a], [b]) => (a < b ? -1 : 1)))
		: value;

/** Whether two drafts save as the same tool, whatever order their edits left the keys in. */
export const sameDraft = (a: CustomToolDraft, b: CustomToolDraft): boolean =>
	JSON.stringify(toPayload(a), keysInOrder) === JSON.stringify(toPayload(b), keysInOrder);

/** Blanks only for the suggestion asked for on its own, so nothing typed meanwhile is lost. */
export const withSuggestion = (
	draft: CustomToolDraft,
	suggestion: CustomToolSuggestion,
	{ blanksOnly, slugLocked }: { blanksOnly: boolean; slugLocked: boolean }
): CustomToolDraft => {
	const pick = (current: string, next: string) => (blanksOnly && current.trim() ? current : next);
	return {
		...draft,
		name: pick(draft.name, suggestion.name),
		slug: slugLocked ? draft.slug : pick(draft.slug, suggestion.slug),
		description: pick(draft.description, suggestion.description)
	};
};

const dropArgs = (step: CustomToolStep, drop: (arg: CustomToolArg) => boolean): CustomToolStep => {
	const kept = Object.entries(step.args).filter(([, arg]) => !drop(arg));
	return kept.length === Object.keys(step.args).length ? step : { ...step, args: Object.fromEntries(kept) };
};

/** `orders_get`, then `orders_get_2`: the tool's name made a valid step id, unique among the steps. */
const stepIdFor = (tool: string, steps: CustomToolStep[]): string => {
	const taken = new Set(steps.map((step) => step.id));
	const cleaned = tool.toLowerCase().replace(/[^a-z0-9_]/g, '_');
	const base = (/^[a-z]/.test(cleaned) ? cleaned : `step_${cleaned}`).slice(0, STEP_ID_LENGTH);
	if (!taken.has(base)) return base;
	for (let n = 2; ; n++) {
		const suffix = `_${n}`;
		const id = `${base.slice(0, STEP_ID_LENGTH - suffix.length)}${suffix}`;
		if (!taken.has(id)) return id;
	}
};

export const withStep = (draft: CustomToolDraft, provider: string, tool: string): CustomToolDraft => ({
	...draft,
	steps: [...draft.steps, { id: stepIdFor(tool, draft.steps), provider, tool, args: {}, after: [] }]
});

const usedInputs = (steps: CustomToolStep[]): Set<string> =>
	new Set(steps.flatMap((step) => Object.values(step.args).flatMap((arg) => (arg.source === 'input' ? [arg.input] : []))));

/** An input no argument uses would only be asked of the AI for nothing. */
const withUnusedInputsDropped = (draft: CustomToolDraft): CustomToolDraft => {
	const used = usedInputs(draft.steps);
	const inputs = draft.inputs.filter((input) => used.has(input.name));
	return inputs.length === draft.inputs.length ? draft : { ...draft, inputs };
};

export const withoutStep = (draft: CustomToolDraft, id: string): CustomToolDraft =>
	withUnusedInputsDropped({
		...draft,
		steps: draft.steps
			.filter((step) => step.id !== id)
			.map((step) => ({
				...dropArgs(step, (arg) => arg.source === 'step' && arg.step === id),
				after: step.after.filter((other) => other !== id)
			}))
	});

/** A step swapped with its neighbour: the order steps are numbered and returned in, not what waits for what. */
export const withStepMoved = (draft: CustomToolDraft, id: string, offset: -1 | 1): CustomToolDraft => {
	const from = draft.steps.findIndex((step) => step.id === id);
	const to = from + offset;
	if (from < 0 || to < 0 || to >= draft.steps.length) return draft;
	const steps = [...draft.steps];
	[steps[from], steps[to]] = [steps[to], steps[from]];
	return { ...draft, steps };
};

/** The steps whose result fields a step takes, each once. */
export const dataSourcesOf = (step: CustomToolStep): string[] => [
	...new Set(Object.values(step.args).flatMap((arg) => (arg.source === 'step' ? [arg.step] : [])))
];

/** The steps a step waits for: the ones it is set to run after, and the ones it takes data from. */
export const dependenciesOf = (step: CustomToolStep): string[] => [...new Set([...step.after, ...dataSourcesOf(step)])];

/** Every step that waits for this one, directly or through others. */
const dependentsOf = (draft: CustomToolDraft, id: string): Set<string> => {
	const found = new Set<string>();
	const visit = (target: string) => {
		for (const step of draft.steps) {
			if (!found.has(step.id) && dependenciesOf(step).includes(target)) {
				found.add(step.id);
				visit(step.id);
			}
		}
	};
	visit(id);
	return found;
};

/** A step may wait for another as long as that one does not already wait for it, which would be a loop. */
export const canWaitFor = (draft: CustomToolDraft, stepId: string, otherId: string): boolean =>
	stepId !== otherId && !dependentsOf(draft, stepId).has(otherId);

/** The steps an argument of this step may take a field from: any other step that does not already wait for it. */
export const candidateSources = (draft: CustomToolDraft, stepId: string): CustomToolStep[] =>
	draft.steps.filter((other) => canWaitFor(draft, stepId, other.id));

/** What a fixed value starts as: yes for a yes-or-no argument, blank for the rest. */
export const fixedValueStart = (field: ToolField): CustomToolArg => ({ source: 'value', value: field.kind === 'boolean' ? true : '' });

export const withAfter = (draft: CustomToolDraft, stepId: string, after: string[]): CustomToolDraft => ({
	...draft,
	steps: draft.steps.map((step) => (step.id === stepId ? { ...step, after: [...new Set(after)] } : step))
});

export const withArg = (draft: CustomToolDraft, stepId: string, field: string, arg: CustomToolArg | null): CustomToolDraft =>
	withUnusedInputsDropped({
		...draft,
		steps: draft.steps.map((step) => {
			if (step.id !== stepId) return step;
			const args = { ...step.args };
			if (arg) args[field] = arg;
			else delete args[field];
			return { ...step, args };
		})
	});

const INPUT_TYPE: Partial<Record<ToolField['kind'], CustomToolInputType>> = {
	integer: 'number',
	number: 'number',
	boolean: 'boolean'
};

/** The type of input the AI passes for a field: a number or a yes or no where the field takes one, text otherwise. */
const inputTypeFor = (field: ToolField): CustomToolInputType => INPUT_TYPE[field.kind] ?? 'string';

export const INPUT_TYPE_LABELS: Record<CustomToolInputType, string> = { string: 'Text', number: 'Number', boolean: 'Yes or no' };

const freeInputName = (inputs: CustomToolInput[], base: string): string => {
	for (let n = 2; ; n++) {
		const name = `${base}_${n}`;
		if (!inputs.some((input) => input.name === name)) return name;
	}
};

/**
 * The AI fills this argument in: an input named after the field, taken from the step tool's schema.
 * An existing input of that name and type is reused, so two steps that need the same value ask the
 * AI for it once, and it becomes required when either step needs it; one of another type gets an
 * input of its own.
 */
export const withAiInput = (draft: CustomToolDraft, stepId: string, field: ToolField): CustomToolDraft => {
	const type = inputTypeFor(field);
	const same = draft.inputs.find((input) => input.name === field.name);
	if (same?.type === type) {
		const inputs = same.required || !field.required ? draft.inputs : draft.inputs.map((input) => (input === same ? { ...input, required: true } : input));
		return withArg({ ...draft, inputs }, stepId, field.name, { source: 'input', input: same.name });
	}
	const input: CustomToolInput = {
		name: same ? freeInputName(draft.inputs, field.name) : field.name,
		type,
		description: field.description ?? '',
		required: field.required
	};
	return withArg({ ...draft, inputs: [...draft.inputs, input] }, stepId, field.name, { source: 'input', input: input.name });
};

const countNamed = (inputs: CustomToolInput[], name: string): number => inputs.filter((input) => input.name === name).length;

const usesInput =
	(name: string) =>
	(arg: CustomToolArg): boolean =>
		arg.source === 'input' && arg.input === name;

/** A rename carries the arguments using the input along, unless another input has either name. */
export const withInputChanged = (draft: CustomToolDraft, index: number, patch: Partial<CustomToolInput>): CustomToolDraft => {
	const before = draft.inputs[index];
	const inputs = draft.inputs.map((input, i) => (i === index ? { ...input, ...patch } : input));
	const after = inputs[index];
	const renamed = before && after.name !== before.name;
	if (!renamed || countNamed(draft.inputs, before.name) > 1 || countNamed(inputs, after.name) > 1) return { ...draft, inputs };
	const uses = usesInput(before.name);
	const renameArg = (arg: CustomToolArg): CustomToolArg => (uses(arg) ? { source: 'input', input: after.name } : arg);
	return {
		...draft,
		inputs,
		steps: draft.steps.map((step) => ({
			...step,
			args: Object.fromEntries(Object.entries(step.args).map(([field, arg]) => [field, renameArg(arg)]))
		}))
	};
};

export const withoutInput = (draft: CustomToolDraft, index: number): CustomToolDraft => {
	const removed = draft.inputs[index];
	const inputs = draft.inputs.filter((_, i) => i !== index);
	if (!removed || countNamed(inputs, removed.name) > 0) return { ...draft, inputs };
	return { ...draft, inputs, steps: draft.steps.map((step) => dropArgs(step, usesInput(removed.name))) };
};

export const slugFormatProblem = (slug: string): string | null => {
	if (slug.startsWith(RESERVED_SLUG_PREFIX)) return `Slugs starting with ${RESERVED_SLUG_PREFIX} are reserved`;
	if (!SLUG.test(slug)) return 'Use 3 to 64 lowercase letters, numbers or _, starting with a letter';
	return null;
};

export const inputNameProblem = (inputs: CustomToolInput[], index: number): string | null => {
	const { name } = inputs[index];
	if (!INPUT_NAME.test(name)) return 'Start with a letter, then letters, numbers or _';
	if (countNamed(inputs, name) > 1) return 'Another input has this name';
	return null;
};

const stepLabel = (index: number): string => `step ${index + 1}`;

const argProblem = (draft: CustomToolDraft, step: CustomToolStep, where: string): string | null => {
	const inputNames = new Set(draft.inputs.map((input) => input.name));
	for (const [field, arg] of Object.entries(step.args)) {
		if (arg.source === 'input' && !inputNames.has(arg.input)) return `Pick where ${field} comes from in ${where}`;
		if (arg.source === 'step' && !STEP_PATH.test(arg.path)) return `Pick a field for ${field} in ${where}`;
	}
	return null;
};

/** A fixed value the step's tool would refuse on every call. */
const fixedValueProblem = (field: ToolField, value: string | number | boolean, where: string): string | null => {
	if ((field.kind === 'number' || field.kind === 'integer') && typeof value !== 'number') return `Enter a number for ${field.name} in ${where}`;
	if (field.kind === 'integer' && !Number.isInteger(value)) return `Enter a whole number for ${field.name} in ${where}`;
	if (field.kind === 'choice' && !field.options.includes(String(value))) return `Pick a value for ${field.name} in ${where}`;
	if (value === '') return `Enter a value for ${field.name} in ${where}`;
	return null;
};

/** A fixed value, or an AI input of another type, that the step's tool would refuse on every call. A field of no plain type is not checked. */
const argValueProblem = (draft: CustomToolDraft, field: ToolField, arg: CustomToolArg, where: string): string | null => {
	if (arg.source === 'value') return fixedValueProblem(field, arg.value, where);
	if (arg.source !== 'input' || field.kind === 'json') return null;
	const type = inputTypeFor(field);
	const input = draft.inputs.find((candidate) => candidate.name === arg.input);
	return input && input.type !== type ? `Use a ${INPUT_TYPE_LABELS[type].toLowerCase()} input for ${field.name} in ${where}` : null;
};

/**
 * The first thing wrong with one step: a required argument unset, an argument whose input or field is
 * missing, a fixed value or input its tool refuses, or an argument the tool no longer takes. While the
 * tool is unknown (`fields` null) only the step's own choices are checked.
 */
const stepProblem = (draft: CustomToolDraft, step: CustomToolStep, fields: ToolField[] | null, where: string): string | null => {
	const missing = fields?.find((field) => field.required && !step.args[field.name]);
	if (missing) return `Set ${missing.name} in ${where}`;
	const problem = argProblem(draft, step, where);
	if (problem || !fields) return problem;
	for (const [name, arg] of Object.entries(step.args)) {
		const field = fields.find((candidate) => candidate.name === name);
		if (!field) return `Clear ${name} in ${where}, which its tool no longer takes`;
		const valueProblem = argValueProblem(draft, field, arg, where);
		if (valueProblem) return valueProblem;
	}
	return null;
};

/** A place in the builder: the inputs the AI passes, or one step. */
export type BuilderPlace = { kind: 'inputs' } | { kind: 'step'; id: string };

export interface BuildIssue {
	message: string;
	/** Where to fix it, when that is one place. */
	at?: BuilderPlace;
}

/**
 * The first thing that keeps the steps from being complete, top to bottom: enough steps, then each
 * step's arguments against its tool's fields, then the inputs.
 */
export const buildIssue = (draft: CustomToolDraft, fieldsOf: (step: CustomToolStep) => ToolField[] | null): BuildIssue | null => {
	if (draft.steps.length < LIMITS.minSteps) {
		const more = LIMITS.minSteps - draft.steps.length;
		return { message: draft.steps.length ? `Add ${more} more ${more === 1 ? 'tool' : 'tools'}` : `Add at least ${LIMITS.minSteps} tools` };
	}
	for (const [index, step] of draft.steps.entries()) {
		const problem = stepProblem(draft, step, fieldsOf(step), stepLabel(index));
		if (problem) return { message: problem, at: { kind: 'step', id: step.id } };
	}
	if (draft.inputs.length > LIMITS.inputs) return { message: `The AI can pass at most ${LIMITS.inputs} inputs`, at: { kind: 'inputs' } };
	if (draft.inputs.some((_, index) => inputNameProblem(draft.inputs, index))) return { message: 'Fix the input names', at: { kind: 'inputs' } };
	return null;
};

/** The first thing that keeps the name, slug and description from saving. */
export const detailsProblem = (draft: CustomToolDraft, slugLocked: boolean): string | null => {
	const name = draft.name.trim();
	const description = draft.description.trim();
	if (!name) return 'Add a name';
	if (name.length > LIMITS.name) return `Keep the name to ${LIMITS.name} characters`;
	if (!slugLocked && slugFormatProblem(draft.slug)) return 'Fix the slug';
	if (!description) return 'Add a description';
	if (description.length > LIMITS.description) return `Keep the description to ${LIMITS.description} characters`;
	return null;
};
